
import {
  useCallback, useEffect, useRef, useState, Fragment,
  useMemo, useContext
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Detail from './Detail';
import Frm from './Frm';
import ExtendedDataGrid from '../dataGrid/ExtendedDataGrid';
import DataGrid from '../dataGrid/DataGrid';
import FilterList from '../dataGrid/filterList/FilterList';
import Tabs from '../TabsWrapper';
import Tab from '../Tab';
import { MetaFunctionsContext } from "../utils/withMeta";
import metaAkcFactory from "./akcFactory";
import {
  processAfter, processBefore,
  getPrimaryField, isDateType, parseValue, processEnumsAfter
} from '../utils/helper';
import { getJoinValue, getField } from '../dataGrid/gridHelper';
import { getUser } from '../utils/storage';
import metaReducer from "./reducer";
import metaComponentFactory from "./componentFactory";
import _ from "lodash";
import LoadingPanel from '../LoadingPanel';
import flags from "../dataGrid/flags";
import { usePrevious } from '../utils/hooks';
import QuestionDialog from '../dialogs/QuestionDialog';
import { MetaStateProvider } from './MetaStateContext';


const DEFAULT_STATE = {
  isLoading: false,
  data: [],
  tabs: [],
  oldData: [],
  total: 0,
  isDetailLoading: false,
  isDetailDisabled: false,
  detailData: {},
  modified: false,
  selected: [],
  printItems: [],
  cellRender: null,
  rowProps: null,
  subGridDetailRow: null,
  sidebarHorizontal: false
};
const DEFAULT_AKC = {
  hidden: flags.ALL,
  disabled: flags.ALL,
  akc: []
};
const DEFAULT_FILTER = { filters: [] };
const DEFAULT_SORT = [];
const DEFAULT_PAGE = { skip: 0, take: 5 };


/**
 * MetaGrid component
 * 
 * @component
 */
const MetaGrid = (props) => {

  const meta = useContext(MetaFunctionsContext);
  const { primaryField, fixedFilter: propsFixedFilter, reducer, componentFactory, db, initFilter: propsInitFilter,
    akcFactory, showMsg, getComponent, filterFactory, currentElFactory, selectAkcFactory, level: propsLevel,
    hidden: propsHidden, disabled: propsDisabled, hiddenBack, frmId, frmValue, frmSchema, parent, onExpandChange, onSelect
  } = props;
  const { getListSuccess, getListFailure, updateSuccess, updateFailure, createSuccess, createFailure,
    getDetailSuccess, getDetailFailure, deleteSuccess, deleteFailure, cancelSuccess, cancelFailure } = props;
  const { getSchema, updateSchema, deleteSchema, getAkc, getList,
    getFilters, getSort, updateSort, getSprav, getTabs, getTlac, get, create, update, getTotal,
    delete: metaDelete, cancel, getUser: metaGetUser, createFilter, updateFilter, deleteFilter, getPageNumber } = meta;

  const ref = useRef(null);

  const [isInitLoading, setIsInitLoading] = useState(true);
  const defaultState = useMemo(() => ({
    primaryField,
    formName: "",
    detailSchema: [],
    gridSchema: [],
    ui: null,
    args: null,
    pages: null,
    allColumns: true,
    akc: DEFAULT_AKC,
    frms: [],
    selectedFrm: null,
    frmFixedFilter: {},
    frmValue: null,
    frmSchema: [],
    frmId: null,
    level: propsLevel || 0,
    enterEdit: true,
    [primaryField]: DEFAULT_STATE,
    deleteText: null,
    cancelText: null,
    tlac: null,
    showAbort: false,
    skipAction: false,
    questions: null,
  }), [primaryField, propsLevel]);
  const [state, setState] = useState(defaultState);
  const currentState = useRef(state);
  const setCurrentState = (newState) => { currentState.current = newState; };
  setCurrentState(state);

  const setData = useCallback((newData) => {
    if (typeof (newData) === "function") {
      setState(prevState => ({ ...prevState, [primaryField]: { ...prevState[primaryField], data: newData(prevState[primaryField].data) } }));
    }
    else {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], data: newData } }));
    }
  }, [primaryField]);

  //#region refs
  const objRef = useRef({});
  const propsRef = useRef(null);
  propsRef.current = props;
  const filter = useRef(DEFAULT_FILTER);
  const sort = useRef(DEFAULT_SORT);
  const page = useRef(props.page || DEFAULT_PAGE);
  const fts = useRef("");
  const setFilter = (newFilter) => { filter.current = newFilter; };
  const setSort = (newSort) => { sort.current = newSort; };
  const setPage = (newPage) => { page.current = newPage; };
  const setFTS = useCallback((newFTS) => {
    fts.current = newFTS;
    const oldPage = page.current;
    const newPage = { ...oldPage, skip: 0 };
    setPage(newPage);
  }, []);

  //#region filters
  const fixedFilter = useRef(propsFixedFilter);
  const setFixedFilter = (newFilter) => { fixedFilter.current = newFilter; };

  const initFilter = useRef(propsInitFilter);
  const setInitFilter = (newFilter) => { initFilter.current = newFilter; };

  const userFilter = useRef([]);
  const [userFilterExtended, setUserFilterExtended] = useState([]); // Filter for deiferent user in filter dialog
  const predefinedFilter = useRef([]);
  const setUserFilter = (newFilter) => { userFilter.current = newFilter; };
  const setPredefinedFilter = (newFilter) => { predefinedFilter.current = newFilter; };
  //#endregion

  const gridEnumColumns = useRef([]);
  const gridDateColumns = useRef([]);
  const detailEnumColumns = useRef([]);
  const cleanDetailData = useRef({});

  const managedUsers = useRef([]);
  const setManagedUsers = (newManagedUsers) => { managedUsers.current = newManagedUsers; };

  //#endregion

  const dataPrimaryField = getPrimaryField(primaryField);

  //total, isLoading, 
  const {
    data, selected, isLoading, total,
    detailData, subGridDetailRow,
    printItems, rowProps, cellRender, modified, oldData, tabs, isDetailDisabled, isDetailLoading
  } = state?.[primaryField] || DEFAULT_STATE;

  const { frms, gridSchema, detailSchema, allColumns,
    ui, args, pages, enterEdit,
    tlac, level, showAbort,
    cancelText, deleteText, formName, sidebarHorizontal
  } = state;

  const dataRef = useRef([]);
  dataRef.current = data;
  const selectedRef = useRef([]);
  selectedRef.current = selected;

  const hidden = propsHidden ?? state.akc.hidden;
  const disabled = propsDisabled ?? state.akc.disabled;

  const isDisabled = useCallback((flag) => (!!(disabled & flag)), [disabled]);
  const isHidden = useCallback((flag) => (!!(hidden & flag)), [hidden]);
  const isHiddenORDisabled = useCallback((flag) => (isHidden(flag) || isDisabled(flag)), [isHidden, isDisabled]);

  const getState = useCallback(() => {
    return _.cloneDeep(currentState.current);
  }, []);

  const handleComponentFactory = useCallback((primaryField, field, value, onChange, obj) => {
    return metaComponentFactory(componentFactory, primaryField, field, value, onChange, { ...obj, showMsg, akc: state.akc });
  }, [componentFactory, showMsg, state.akc]);

  const getObj = useCallback((akc, isDetailAkc = false) => {
    return {
      ...objRef.current,
      filter: filter.current,
      sort: sort.current,
      page: page.current,
      akc: akc,
      isDetailAkc: isDetailAkc
    };
  }, []);

  const getReducerState = useCallback(async (action, data, state) => {
    const reducerState = await metaReducer(action, { ...data, obj: getObj() }, { ...state, primaryField }, getState(), reducer);
    return reducerState;
  }, [getObj, getState, primaryField, reducer]);

  const setReducerState = useCallback((reducerState) => {
    const oldState = currentState.current;
    const patch = {};
    for (const [k, v] of Object.entries(reducerState)) {
      if (k === 'primaryField' || k === primaryField) continue;
      if (!_.isEqual(v, oldState[k])) {
        patch[k] = v;
      }
    }
    const nextPF = reducerState[primaryField];
    if (nextPF && typeof nextPF === 'object') {
      const oldPF = oldState[primaryField] || {};
      const subPatch = {};
      for (const [k2, v2] of Object.entries(nextPF)) {
        if (!_.isEqual(v2, oldPF[k2])) {
          subPatch[k2] = v2;
        }
      }
      if (Object.keys(subPatch).length) {
        patch[primaryField] = subPatch;
      }
    }

    if (Object.keys(patch).length) {
      setState(prev => ({
        ...prev,
        ...patch,
        ...(patch[primaryField]
          ? { [primaryField]: { ...prev[primaryField], ...patch[primaryField] } }
          : null)
      }));
    }
  }, [primaryField]);

  const processReducer = useCallback(async (action, data) => {
    const newState = await getReducerState(action, data, getState());
    setReducerState(newState);
  }, [getReducerState, setReducerState, getState]);


  const processReducerState = useCallback(async (action, data, value) => {
    const gettedState = await getReducerState(action, data, value);
    setReducerState(gettedState);
    return gettedState;
  }, [getReducerState, setReducerState]);

  const handleSelect = useCallback(async (newSelected) => {
    await processReducerState("SELECT", {}, { [primaryField]: { selected: newSelected } });
    if (onSelect) {
      onSelect(newSelected);
    }
  }, [primaryField, processReducerState, onSelect]);

  const reselectItems = useCallback(async (data, dataPrimaryField) => {
    if (dataPrimaryField === primaryField) {
      const _primaryField = getPrimaryField(primaryField);
      handleSelect(data.filter((x) => selectedRef.current?.map(x => x[_primaryField]).includes(x[_primaryField])));
    }
  }, [primaryField, handleSelect]);

  //#region enums
  const processEnums = useCallback((res, listPrimaryField) => {
    if (primaryField === listPrimaryField) {
      res.Data.Items.forEach(x => {
        gridEnumColumns.current.forEach(column => {
          const value = currentState.current[column.dataField]?.find(el => el[column.primaryField] === _.get(x, column.field) || el[column.primaryField] === _.get(x, column.field + "." + column.primaryField)) || null;
          _.set(x, column.field, value);
        });
      });
    }
    else {
      currentState.current.detailSchema.forEach((x) => {
        if (x.primaryField === listPrimaryField && primaryField !== listPrimaryField) {
          let enumColumns = x.schema.filter(x => x.type === 'enum' || x.type === 'buttongroup' || x.type === 'radio');
          let newState = {};
          enumColumns.map((x) => {
            let data = (!x.required && x.enumData) ? [{ [getPrimaryField(x.primaryField)]: null, [x.textField]: "" }, ...x.enumData] : x.enumData;
            newState = { ...newState, [`listGrid${x.field}`]: data, [`list${x.field}`]: { data: data } };
            return x;
          });

          setState(prev => ({ ...prev, newState }));
          res.Data.Items.forEach(x => {
            enumColumns.forEach(column => {
              const dataField = `listGrid${column.field}`;
              if (typeof (x[column.field]) !== 'object') {
                const value = newState[dataField].find(el => el[column.primaryField] === _.get(x, column.field) || el[column.primaryField] === _.get(x, column.field + "." + column.primaryField)) || null;
                _.set(x, column.field, value);
              }
            });
          });
        }
      });
    }
  }, [primaryField]);

  const processEnumsDetail = (data) => {
    detailEnumColumns.current.forEach(column => {
      if (typeof (data[column.field]) !== 'object') {
        const value = currentState.current[column.dataField].data.find(el => el[column.primaryField] === _.get(data, column.field) || el[column.primaryField] === _.get(data, column.field + "." + column.primaryField)) || null;
        _.set(data, column.field, value);
      }
    });
  };
  //#endregion

  //#region CRUD

  //#region GET
  const handleGetTotal = useCallback(async (filter, primaryField, fullTextSearch = "") => {
    try {
      const res = await getTotal(primaryField, filter, db, fullTextSearch ?? fts.current);
      if (res?.Data?.TotalRecords) {
        await processReducerState('GET_TOTAL', {}, { [primaryField]: { ...currentState.current[primaryField], total: res.Data.TotalRecords } });
      }
    }
    catch (error) {
      getListFailure(error);
    }
  }, [getTotal, getListFailure, processReducerState, db]);

  const handleGetList = useCallback(async (filter, sort, page, primaryField, fts = "") => {
    const f = filterFactory ? filterFactory(primaryField, filter) : filter;
    try {
      await processReducerState("GET_LIST_BEFORE", { primaryField }, { [primaryField]: { data: [], total: 0, isLoading: true }, showAbort: true });
      const res = await getList(primaryField, f, sort, page, db, fts);
      if (gridDateColumns.current.length > 0) {
        res.Data.Items.map(x => {
          x = processAfter(gridDateColumns.current, x);
          return x;
        });
      }
      processEnums(res, primaryField);
      await processReducerState("GET_LIST", { primaryField }, { [primaryField]: { data: res.Data.Items, total: res.Data.TotalRecords, isLoading: false, oldData: res.Data.Items }, showAbort: false });
      getListSuccess(res);
      reselectItems(res.Data.Items, primaryField);
      await handleGetTotal(f, primaryField, fts);
    }
    catch (error) {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], data: [], total: 0, isLoading: false }, showAbort: false }));
      getListFailure(error);
    }
  }, [getList, getListSuccess, getListFailure, processReducerState, processEnums, filterFactory, handleGetTotal, db, reselectItems]);

  const handleDefaultGetList = useCallback(async () => {
    await handleGetList(filter.current, sort.current, page.current, primaryField, fts.current);
  }, [handleGetList, primaryField]);

  const _get = useCallback(async (primaryField, id) => {
    try {
      let res = await get(primaryField, id, db);
      const schema = await getSchema(primaryField, db);
      if (res?.Data && res.Data !== null) {
        res.Data = processAfter(schema?.Detail, res.Data);
        res.Data = processEnumsAfter(schema?.Detail, res.Data);
        return res.Data;
      }
      else {
        return null;
      }
    }
    catch (error) {
      return null;
    }
  }, [get, getSchema, db]);

  const handleGet = useCallback(async (val) => {
    try {
      let res;
      if (allColumns) {
        res = (typeof (val) == 'object' ? val : { Data: currentState.current[primaryField].data.find(x => x[dataPrimaryField] === val) });
      } else {
        setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: true } }));
        res = await get(primaryField, val, db);
      }
      if (!res?.Data) {
        res = { Data: cleanDetailData.current };
      }
      else {
        const detailSchema = currentState.current.detailSchema;
        res.Data = processAfter(detailSchema, res.Data || {});
      }
      processEnumsDetail(res.Data);

      const newState = { [primaryField]: { modified: false, isDetailLoading: false, isDetailDisabled: false, detailData: res.Data } };
      let reducerState = await getReducerState('GET', {}, newState);
      if (res.Data[dataPrimaryField] === null) {
        reducerState = await getReducerState('GET_CREATE', {}, reducerState);
      }
      setReducerState(reducerState);
      await getDetailSuccess(res);
    }
    catch (error) {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], modified: false, isDetailLoading: false, isDetailDisabled: false } }));
      getDetailFailure(error);
    }
  }, [dataPrimaryField, primaryField, get, db, getReducerState, setReducerState, getDetailSuccess, getDetailFailure, allColumns]);
  //#endregion

  //#region SELECT

  const getListAndSelect = useCallback(async (id) => {
    const selected = selectedRef.current;
    await handleGetList(filter.current, sort.current, page.current, primaryField);
    const newSelected = currentState.current[primaryField].data.filter(x => id === x[dataPrimaryField]);
    if (!_.isEqual(newSelected, selected)) {
      await handleSelect(newSelected);
    }
    await new Promise(resolve => setTimeout(resolve, 0));
    await processReducerState('GET', {}, { ...currentState.current });
  }, [primaryField, dataPrimaryField, handleGetList, handleSelect, processReducerState]);
  //#endregion

  const handleCreate = useCallback(async (d) => {
    try {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: true, isDetailDisabled: true } }));
      const reducerState = await getReducerState("CREATE_BEFORE", { data: d }, { [primaryField]: { detailData: d }, skipAction: false });
      if (reducerState.questions?.list.length > 0) {
        setState(prev => ({ ...prev, questions: reducerState.questions }));
      }
      if (reducerState.skipAction) {
        setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: false, isDetailDisabled: false }, skipAction: false }));
        return;
      }
      
      const detailSchema = currentState.current.detailSchema;
      let newData = processBefore(detailSchema, reducerState[primaryField].detailData);
      newData[dataPrimaryField] = 0;

      let res = await create(primaryField, newData, db);
      res.Data = processAfter(detailSchema, res.Data);
      processEnumsDetail(res.Data);

      await processReducerState("CREATE", { data: res.Data }, { [primaryField]: { detailData: res.Data, modified: false, isDetailLoading: false, isDetailDisabled: false } });
      createSuccess(res);
      const id = res.Data[dataPrimaryField];

      await getListAndSelect(id);

      const pageNumber = await getPageNumber(primaryField, id, filter.current, sort.current, page.current, db);
      const newPage = { take: page.current.take, skip: page.current.take * (pageNumber.Data.Page || 1) - page.current.take };
      setPage(newPage);

      return id;
    }
    catch (error) {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], modified: false, isDetailLoading: false, isDetailDisabled: false } }));
      createFailure(error);
    }
  }, [primaryField, dataPrimaryField, create, createSuccess, createFailure, processReducerState, db, getListAndSelect, getPageNumber]);

  const handleUpdate = useCallback(async (id, d) => {
    if (id === 0) {
      return await handleCreate(d);
    }

    try {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: true, isDetailDisabled: true } }));
      const reducerState = await getReducerState("UPDATE_BEFORE", { data: d }, { [primaryField]: { detailData: d }, skipAction: false });
      if (reducerState.questions?.list.length > 0) {
        setState(prev => ({ ...prev, questions: reducerState.questions }));
      }
      if (reducerState.skipAction) {
        setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: false, isDetailDisabled: false }, skipAction: false }));
        return;
      }

      const detailSchema = currentState.current.detailSchema;
      const newData = processBefore(detailSchema, reducerState[primaryField].detailData);

      let res = await update(primaryField, id, newData, db);
      res.Data = processAfter(detailSchema, res.Data);
      processEnumsDetail(res.Data);

      const newGridData = dataRef.current.map(item => item[dataPrimaryField] === res.Data[dataPrimaryField] ? { ..._.omit(item, ['inEdit', 'hasChanged']), ...res.Data } : item);

      await processReducerState("UPDATE", { data: res.Data }, { [primaryField]: { detailData: res.Data, data: newGridData, oldData: newGridData, modified: false, isDetailLoading: false, isDetailDisabled: false } });
      updateSuccess(res);
      await getListAndSelect(id);
    }
    catch (error) {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], modified: false, isDetailLoading: false, isDetailDisabled: false, } }));
      updateFailure(error);
    }
  }, [primaryField, dataPrimaryField, update, handleCreate, updateSuccess, updateFailure, processReducerState, getListAndSelect]);

  const handleDelete = useCallback(async (id) => {
    try {
      if ((await processReducerState("DELETE_BEFORE", { id: id }, { skipAction: false })).skipAction) {
        setState(prev => ({ ...prev, skipAction: false }));
        return;
      }
      const res = await metaDelete(primaryField, id, db);
      deleteSuccess(res);
    }
    catch (error) {
      deleteFailure(error);
    }
  }, [primaryField, db, metaDelete, deleteSuccess, deleteFailure, processReducerState]);
  //#endregion

  const changeField = useCallback(async (name, value) => {
    const newDetailData = _.cloneDeep(currentState.current[primaryField].detailData);
    const oldValue = _.get(newDetailData, name);
    _.set(newDetailData, name, value);
    const newState = { [primaryField]: { modified: true, detailData: newDetailData } };
    let reducerState = await getReducerState('CHANGE', { field: name, value: value, oldValue: oldValue }, newState);
    if (dataPrimaryField === name && value === 0 && oldValue !== 0) {
      reducerState = await getReducerState('CHANGE_COPY', {}, reducerState);
    }
    setReducerState(reducerState);
  }, [primaryField, dataPrimaryField, getReducerState, setReducerState]);

  const discardChanges = useCallback(async () => {
    processReducerState('DISCARD_CHANGES', {}, { [primaryField]: { modified: false } });
  }, [processReducerState, primaryField]);

  const blur = useCallback(async (field, value) => {
    processReducerState('BLUR', { field, value }, {});
  }, [processReducerState]);

  const handleItemChange = useCallback(async (event, newData) => {
    const newState = {
      [primaryField]: {
        data: newData.map(x => x[dataPrimaryField] === event.dataItem[dataPrimaryField] ? { ...x, [event.field]: event.value, hasChanged: event.hasChanged } : x),
        modified: true
      }
    };
    const dataItem = newState[primaryField].data.find(x => x[dataPrimaryField] === event.dataItem[dataPrimaryField]);
    const reducerState = await processReducerState('ITEM_CHANGE', { field: event.field, value: event.value, dataItem: dataItem }, newState);
    return reducerState[primaryField].data;
  }, [processReducerState, primaryField, dataPrimaryField]);

  const getPickerValue = useCallback(async (primaryField, defaultValue) => {
    const id = parseValue(defaultValue, 'container');
    if (id === parseInt(id, 10)) {
      const res = await get(primaryField, id, db);
      if (res && res.Data) {
        //detailLoading -> false
        return res.Data;
      }
    }
    return null;
  }, [get, db]);

  const getValue = useCallback(async (x, newState) => {
    const { defaultValue } = x;

    switch (x.type) {
      case "enum":
      case "buttongroup":
      case "radio":
        const id = parseValue(defaultValue, "enum");
        return defaultValue ? newState[x.dataField].data?.find(e => e[x.primaryField ?? "I_HOD"] === id) : null;
      case "container":
        return defaultValue ? await getPickerValue(x.primaryField, defaultValue) : null;
      case "boolean":
        return parseValue(defaultValue, "boolean") || false;
      default:
        return parseValue(defaultValue, x.type);
    }
  }, [getPickerValue]);

  //#region schema

  const onGetSchema = useCallback(async (primaryField, db) => {
    const newSchema = await getSchema(primaryField, db);
    return newSchema;
  }, [getSchema]);

  const handleGetSchema = useCallback(async (primaryField) => {
    const newSchema = await onGetSchema(primaryField, db);
    const gridSchema = newSchema.Grid;
    const detailSchema = newSchema.Detail;
    const ui = newSchema?.Args?.ui;
    const args = _.omit(newSchema?.Args, "ui");
    const frms = newSchema.Frms;
    const pages = newSchema.Pages;
    const allColumns = newSchema.AllColumns;
    const formName = newSchema.NFrm;

    let newState = { gridSchema, detailSchema, Id: newSchema.Id, ui, args, frms, pages, allColumns, formName };

    gridDateColumns.current = gridSchema.filter(x => isDateType(x.filter) || x.type === "daterange" || x.type === "21");
    gridEnumColumns.current = gridSchema.filter(x => x.type === 'enum' || x.type === 'buttongroup' || x.type === 'radio');
    detailEnumColumns.current = detailSchema.filter(x => x.type === 'enum' || x.type === 'buttongroup' || x.type === 'radio');

    gridEnumColumns.current.forEach((x) => {
      x.primaryField = getPrimaryField(x.primaryField);
      x.dataField = `listGrid${x.field}`;
      x.data = `list${x.field}`;
    });
    detailEnumColumns.current.forEach((x) => {
      x.primaryField = getPrimaryField(x.primaryField);
      x.dataField = `list${x.field}`;
    });

    const mergedEnumColumns = _.unionBy(gridEnumColumns.current, detailEnumColumns.current, "field");
    for (const x of mergedEnumColumns) {
      if (x.primaryField && x.primaryField !== "I_HOD" && !x.enumData) {
        const res = await getList(x.primaryField, DEFAULT_FILTER, DEFAULT_SORT, { ...DEFAULT_PAGE, take: 1000 }, db);
        x.enumData = res.Data ? res.Data.Items : [];
      }
      let data = (!x.required && x.enumData) ? [{ [getPrimaryField(x.primaryField)]: null, [x.textField]: "" }, ...x.enumData] : x.enumData;
      newState = { ...newState, [`listGrid${x.field}`]: data, [`list${x.field}`]: { data: data } };
    }

    if (frmSchema) {
      detailSchema.forEach(x => {
        const field = frmSchema.find(s => s.field === x.field);
        if (field && !_.isNil(field.disabled)) {
          x.disabled = field.disabled;
        }
        if (field && !_.isNil(field.defaultValue)) {
          x.defaultValue = field.defaultValue;
        }
      });
    }

    for (const x of detailSchema) {
      if (x.field.includes('.')) continue;
      const defaultValue = await getValue(x, newState);
      _.set(cleanDetailData.current, x.field, defaultValue);
    }

    if (frmValue) {
      cleanDetailData.current = { ...cleanDetailData.current, ...frmValue };
    }

    return newState;
  }, [frmValue, frmSchema, onGetSchema, getList, db, getValue]);

  const handleChangeSchema = useCallback(async (value) => {
    updateSchema(primaryField, { Grid: value }, null);
    processReducerState("CHANGE_SCHEMA", {}, { gridSchema: value });
  }, [primaryField, updateSchema, processReducerState]);
  //#endregion

  const handleGetAkc = useCallback(async (id) => {
    const akc = await getAkc(id, frmId !== undefined, db);
    return akc;
  }, [frmId, getAkc, db]);

  const handleGetTabs = useCallback(async (primaryField) => {
    try {
      const tabs = await getTabs(primaryField, db);
      return tabs;
    }
    catch (err) {
      return [];
    }
  }, [getTabs, db]);

  const handleGetTlac = useCallback(async (primaryField) => {
    const tlac = await getTlac(primaryField);
    return tlac;
  }, [getTlac]);

  const getDefaultUserId = useCallback(async () => {
    if (db) {
      return await metaGetUser(db);
    }
    const user = getUser();
    return user?.I_UZ;
  }, [db, metaGetUser]);

  //#region filter

  const handleGetFilter = useCallback((filterData, frms = currentState.current.frms) => {
    const newInitFilter = filterData.filter(x => !x.disabled && x.CanDisabled);

    let newFilter = propsInitFilter || {
      filters: newInitFilter.map(x => x.filters).flat()
    };

    newFilter.filters = newFilter.filters.map(x => {
      if (x.frmPod) {
        const frm = frms.find(f => f.Id === x.frmPod);
        x.joins = frm.Joins.map(join => {
          return {
            childField: join.Field,
            parentField: join.ParentField,
            operator: join.Operator,
            childStl: join.ChildFieldStl,
            childId: join.ChildFieldId
          };
        });
      }
      return x;
    });
    const newFixedFilter = {
      filters: [
        ...(propsFixedFilter ? propsFixedFilter.filters : []),
        ...filterData.filter(x => !x.disabled && !x.CanDisabled).map(x => x.filters).flat()
      ]
    };

    setFilter(newFilter);
    setInitFilter(newInitFilter);
    setFixedFilter(newFixedFilter);

  }, [JSON.stringify(propsFixedFilter), JSON.stringify(propsInitFilter)]);

  const handleGetFilterList = useCallback(async (filterData, iUz) => {
    const newUserFilter = filterData.filter(x => x.CanDisabled && x.type !== 5);
    const newPredefinedFilter = filterData.filter(x => x.CanDisabled && x.type === 5);
    const defaultUserId = await getDefaultUserId();
    if (iUz === null || iUz === defaultUserId) {
      setUserFilter(newUserFilter);
    }
    setUserFilterExtended(newUserFilter);
    setPredefinedFilter(newPredefinedFilter);
    return newUserFilter;
  }, [getDefaultUserId]);

  const handleGetFilters = useCallback(async (primaryField, schema, iUz = null, frms = null) => {
    const filterData = await getFilters(primaryField, schema, iUz, db);
    if (iUz === null) {
      await handleGetFilter(filterData, frms);
    }
    return await handleGetFilterList(filterData, iUz);
  }, [getFilters, handleGetFilter, handleGetFilterList, db]);
  //#endregion

  //#region sort
  const handleGetSort = useCallback(async (primaryField, schema) => {
    const newSort = await getSort(primaryField, schema, db);
    setSort(newSort);
  }, [getSort, db]);

  const handleUpdateSort = useCallback(async (sort, schema) => {
    const newSort = [...sort, ...schema.map(col => ({ field: getField(col), disabled: true })).filter(col => !sort.find(s => s.field === col.field))];
    await updateSort(primaryField, newSort, db);
  }, [updateSort, primaryField, db]);

  //#endregion

  const handleGetManagedUsers = useCallback(async () => {
    let res = await getSprav(null);
    setManagedUsers(res);
  }, [getSprav]);

  const init = useCallback(async (primaryField) => {
    try {
      setIsInitLoading(true);
      cleanDetailData.current = {};
      const newSchema = await handleGetSchema(primaryField);

      const [akc, tabs, tlac] = await Promise.all([
        handleGetAkc(frmId ?? newSchema.Id),
        handleGetTabs(primaryField),
        handleGetTlac(primaryField),
        handleGetFilters(primaryField, newSchema.gridSchema, null, newSchema.frms),
        handleGetSort(primaryField, newSchema.gridSchema),
        handleGetManagedUsers(),
      ]);

      setFTS("");

      await processReducerState("GET_SCHEMA", {}, { ...newSchema, akc, tlac, [primaryField]: { detailData: cleanDetailData.current, printItems: [], tabs, selected: [] } });
      setIsInitLoading(false);
    }
    catch (e) {
      //TODO: Čo urobiť?
      setIsInitLoading(false);
      console.error(e);
    }

  }, [frmId, handleGetSchema, handleGetAkc, handleGetTabs, handleGetTlac, handleGetFilters, handleGetSort, handleGetManagedUsers, processReducerState, setFTS]);

  useEffect(() => {
    setState(defaultState);
    init(primaryField);
  }, [primaryField, init, defaultState]);

  const handleChange = useCallback((filter, sort, page) => {
    setFilter(filter);
    setSort(sort);
    setPage(page);
  }, []);

  const handleChangeFrm = useCallback((name, data) => {
    const selected = selectedRef.current;
    let filters = [], frmValue = null, frmId = null, frmSchema = data?.Args?.frmSchema ?? [];
    if (selected?.[0] && data?.Joins) {
      frmValue = {};
      frmId = data.Id;
      const v = selected[0];

      data.Joins.forEach(y => {
        const x = structuredClone(y);
        let filterValue = "";
        let addToFrm = true;
        if (x.Value !== null) {
          let toReplace = x.Value.match(/{([^}]+)}/g);
          if (!toReplace) {
            _.set(frmValue, x.Field, parseValue(x.Value, x.Type));
            addToFrm = false;
          }
          else {
            let finalString = x.Value;
            if (toReplace) {
              toReplace.forEach((element) => {
                let propName = element.replace(/[{}]/g, "");
                const replaceWith = _.get(v, propName);
                finalString = finalString.replace(
                  element,
                  replaceWith != null ? replaceWith : ""
                );
              });
            }

            filterValue = finalString;
            let valueArray = filterValue.split(",");
            if (valueArray.some(x => x !== valueArray[0])) {
              addToFrm = false;
            } else {
              filterValue = valueArray[0];
              x.Operator = (x.Operator === "in" || x.Operator === "notin") ? "eq" : x.Operator;
            }
          }
        }
        if (addToFrm && ["eq", "in"].includes(x.Operator)) {
          let value;
          if (x.ParentField && x.ParentField.includes('.')) {
            if (x.Field && x.Field.includes('.')) {
              value = v[x.ParentField.split('.')[0]];
            } else {
              value = _.get(v, x.ParentField, null);
            }
          } else if (x.Field && x.Field.includes('.')) {
            value = v;
          } else {
            value = v[x.ParentField];
          }
          const field = x.Field.split('.')[0];
          frmValue[field] = value;
          const frmField = { ...(frmSchema.find(s => s.field === field) || { field: field }), disabled: true };
          frmSchema = [...frmSchema.filter(s => s.field !== field), frmField];
        }

        filters.push({
          field: x.Field,
          joins: [{ parentField: x.ParentField }],
          value: filterValue !== "" ? filterValue : getJoinValue(x, selected),
          operator: x.Operator ? x.Operator.split('-')[0] : 'in',
          filterType: 1,
        });
      });

      frmSchema.forEach(x => {
        x.defaultValue = _.toString(_.get(v, x.parentPath, x.defaultValue));
      });
    }

    setState(prev => ({ ...prev, frmValue, frmSchema, selectedFrm: data, frmFixedFilter: { filters }, frmId }));
  }, []); //primaryField, getJoinValue

  const handlePrint = useCallback(async (index, name, newTlac) => {
    await processReducer('PRINT', { name, value: true, tlac: newTlac });
  }, [processReducer]);

  const handleCancel = useCallback(async (id) => {
    try {
      if ((await processReducerState("CANCEL_BEFORE", { id: id }, { skipAction: false })).skipAction) {
        setState(prev => ({ ...prev, skipAction: false }));
        return;
      }
      const res = await cancel(primaryField, id, db);
      cancelSuccess(res);
    }
    catch (error) {
      cancelFailure(error);
    }
  }, [primaryField, cancel, cancelFailure, cancelSuccess, db, processReducerState]);

  const handleReset = useCallback(async (value) => {
    await deleteSchema(primaryField, value);
    init(primaryField);
  }, [deleteSchema, init, primaryField]);

  const onSubGridDetailRow = useCallback((props) => {
    return subGridDetailRow({ ...props, obj: getObj() });
  }, [subGridDetailRow, getObj]);

  const expandChange = useCallback((event) => {
    const id = parseInt(_.keys(event.detailExpand)[0]);

    setData(dataRef.current.map(x => {
      if (x[dataPrimaryField] === id) {
        if (event.detailExpand[id]) {
          x.expanded = true;
        } else
          _.omit(x, "expanded");
      }
      return x;
    }));

    if (onExpandChange) {
      onExpandChange(event.dataItem);
    }
  }, [dataPrimaryField, onExpandChange, setData]);

  const isCellEdit = useMemo(() => !isHiddenORDisabled(flags.GRID_CELL_EDIT) && !isHiddenORDisabled(flags.GRID) && isHiddenORDisabled(flags.GRID_INLINE_EDIT), [isHiddenORDisabled]);
  const isInlineEdit = useMemo(() => !isHiddenORDisabled(flags.GRID_INLINE_EDIT) && !isHiddenORDisabled(flags.GRID) && isHiddenORDisabled(flags.GRID_CELL_EDIT), [isHiddenORDisabled]);

  const handleAdd = useCallback(async () => {
    const item = {
      ...cleanDetailData.current,
      [dataPrimaryField]: 0,
      inEdit: (!isInlineEdit && isCellEdit) ? (state?.gridSchema?.find(f => f.type === "text" || f.type === "string")?.field ?? true) : true,
      hasChanged: true
    };
    const data = dataRef.current;
    await processReducerState('ADD', {}, { [primaryField]: { isLoading: false, modified: true, data: [item, ...data] } });

    await handleSelect([item]);

    //Po pridaní autofocus, aby sa dalo v bunke rovno písať
    setTimeout(() => {
      const dummy = document.querySelector('tr.k-master-row.k-selected.k-grid-edit-row td.k-grid-edit-cell:not(.column-menu) input');
      if (dummy && dummy.type === "text") {
        dummy.focus();
      }
    }, 0);

  }, [primaryField, dataPrimaryField, isInlineEdit, isCellEdit, processReducerState, handleSelect]);

  const handleCopy = useCallback(async () => {
    const selected = selectedRef.current;
    const item = { ...selected[0], [dataPrimaryField]: 0, inEdit: true, hasChanged: true };
    const data = dataRef.current;
    const newState = { [primaryField]: { isLoading: false, modified: true, data: [item, ...data] } };
    //ADD in handleCopy?
    await processReducerState('ADD', {}, newState);

    await handleSelect([item]);
    //Po pridaní autofocus, aby sa dalo v bunke rovno písať
    setTimeout(() => {
      const dummy = document.querySelector('tr.k-master-row.k-selected.k-grid-edit-row td.k-grid-edit-cell:not(.column-menu) input');
      if (dummy && dummy.type === "text") {
        dummy.focus();
      }
    }, 0);

  }, [primaryField, dataPrimaryField, processReducerState, handleSelect]);

  const handleAbortChanges = useCallback(async (dataItem) => {
    const oldData = currentState.current[primaryField].oldData;
    const originalItem = oldData.find(item => item[dataPrimaryField] === dataItem?.[dataPrimaryField]);
    let newData = dataRef.current.filter(x => x[dataPrimaryField] !== 0);
    if (originalItem) {
      newData = dataRef.current.map(item => item[dataPrimaryField] === originalItem[dataPrimaryField] ? originalItem : item);
    }
    processReducerState('ABORT_CHANGES', {}, { [primaryField]: { isLoading: false, data: newData } });
  }, [primaryField, dataPrimaryField, processReducerState]);

  const handleAbortChangesAll = useCallback(async () => {
    const oldData = currentState.current[primaryField].oldData;
    const data = oldData.map(item => { return _.omit(item, ['inEdit', 'hasChanged']); }).filter(x => x[dataPrimaryField] !== 0);
    processReducerState('ABORT_CHANGES_ALL', {}, { [primaryField]: { isLoading: false, data: data } });
    await handleSelect([]);
  }, [primaryField, dataPrimaryField, processReducerState, handleSelect]);

  const handleEnterEdit = useCallback(async (dataItem, field) => {
    const reducerState = await processReducerState('ENTER_EDIT', { dataItem, field }, { enterEdit });
    return reducerState.enterEdit;
  }, [processReducerState, enterEdit]);

  const handleExitEdit = useCallback(async (dataItem, field, data) => {
    await processReducerState('EXIT_EDIT', { dataItem, field, data }, {}); //[primaryField]: { oldData: null }
  }, [processReducerState]);

  //#region UserFilters
  const handleCreateFilter = useCallback(async (data, iUz = null) => {
    await createFilter(primaryField, data, state.gridSchema, iUz, db);
    return await handleGetFilters(primaryField, state.gridSchema, iUz);
  }, [createFilter, primaryField, db, handleGetFilters, state.gridSchema]);

  const handleUpdateFilter = useCallback(async (id, disabled, type, iUz) => {
    await updateFilter(primaryField, id, disabled, type, iUz, db);
    await handleGetFilters(primaryField, state.gridSchema, iUz);
  }, [updateFilter, primaryField, db, handleGetFilters, state.gridSchema]);

  const handleDeleteFilter = useCallback(async (id, iUz) => {
    await deleteFilter(primaryField, id, iUz, db);
    await handleGetFilters(primaryField, state.gridSchema, iUz);
  }, [deleteFilter, primaryField, db, handleGetFilters, state.gridSchema]);
  //#endregion

  const removeFilter = (newFilter) => {
    handleChange(newFilter, sort.current, page.current);
    handleDefaultGetList();
  };

  //#region actions
  const getSelectActions = useCallback(() => {
    if (selectAkcFactory) {
      return selectAkcFactory(primaryField, showMsg, handleSelect);
    }
    else return undefined;
    // eslint-disable-next-line
  }, [selectAkcFactory, showMsg, handleSelect]);

  const getActions = useCallback(() => {
    return state.akc.akc.filter(a => !a.Hidden && [1, 3].includes(a.Umiest))
      .sort((a, b) => a.PorMriez - b.PorMriez)
      .map(a =>
        <Fragment key={'akc-' + a.IAkc}>{metaAkcFactory(a, primaryField, showMsg, getObj(a), akcFactory)}</Fragment>
      );
  }, [state.akc, akcFactory, getObj, showMsg, primaryField]);

  const getDetailActions = useCallback(() => {
    return state.akc.akc.filter(a => [2, 3].includes(a.Umiest))
      .sort((a, b) => a.PorDet - b.PorDet)
      .map(a =>
        <Fragment key={'akc-' + a.IAkc}>{metaAkcFactory(a, primaryField, showMsg, getObj(a, true), akcFactory)}</Fragment>
      );
  }, [state.akc, akcFactory, getObj, showMsg, primaryField]);
  //#endregion

  const getParentProps = useCallback((currentState, defaultState) => {
    const addedFields = { ...currentState };

    Object.keys(defaultState).forEach(key => {
      delete addedFields[key];
    });

    return { ...addedFields, getList: handleGetList, updateData: handleUpdate, handleChangeFrm: handleChangeFrm, forPodr: parent?.state[parent.state.primaryField]?.detailData };
  }, [handleGetList, handleUpdate, handleChangeFrm]);

  const parentPropsHelp = getParentProps(state, defaultState);
  const parentProps = useMemo(() => parentPropsHelp, [JSON.stringify(parentPropsHelp)]);  // eslint-disable-line react-hooks/exhaustive-deps
  const isUserFilter = !isHiddenORDisabled(flags.FILTER) && !isHiddenORDisabled(flags.USER_FILTER);
  const userFilterList = useMemo(() => isUserFilter ? userFilter.current : [], [isUserFilter, userFilter]);

  const gridProps = useMemo(() => ({
    ref: ref,
    primaryField: primaryField,
    className: primaryField,
    isLoading: isLoading,
    changeTabDisable: false,
    data: data,
    onGet: handleGet,
    onGetList: handleDefaultGetList,
    getList: handleGetList,
    onChange: handleChange,
    onDelete: handleDelete,
    schema: gridSchema || [],
    onChangeSchema: handleChangeSchema,
    sort: sort.current,
    updateSort: handleUpdateSort,
    page: page.current,
    total: total,
    selected: selected,
    onSelect: handleSelect,
    filter: filter.current,
    fixedFilter: fixedFilter.current,
    userFilterList: userFilterList,
    userFilterExtended: isUserFilter ? userFilterExtended : [],
    predefinedFilter: predefinedFilter.current,
    handleGetFilters: handleGetFilters,
    createFilter: handleCreateFilter,
    updateFilter: handleUpdateFilter,
    deleteFilter: handleDeleteFilter,
    filterFactory: filterFactory,
    fts: fts.current,
    frms: frms,
    toolbarActions: getActions,
    actions: getSelectActions,
    hidden: hidden,
    disabled: disabled,
    handleReset: handleReset,
    multiple: !isHidden(flags.ONLY_SELECT),
    managedUsers: managedUsers.current,
    getDefaultUserId: getDefaultUserId,
    handleChangeFrm: handleChangeFrm,
    onPrint: handlePrint,
    onGetSchema: onGetSchema,
    onCancel: handleCancel,
    detail: subGridDetailRow ? onSubGridDetailRow : null,
    onExpandChange: expandChange,
    tlac: tlac,
    showAbort: showAbort,
    cancelText: cancelText,
    deleteText: deleteText,
    db: db,
    componentFactory: handleComponentFactory,
    reducer: reducer,
    deleteAll: true,
    reset: true,
    printable: true,
    printItems: printItems,
    rowProps: rowProps,
    cellRender: cellRender,
    onChangeField: changeField,
    allColumns: allColumns,
    parent: parent,
    parentProps: parentProps,
    hiddenBack: hiddenBack,
    setData: setData,
    args: args,
    showMsg
  }), [allColumns, cancelText, cellRender, changeField, data, db, deleteText,
    disabled, expandChange, filterFactory, frms, getActions, getDefaultUserId, gridSchema, handleCancel, handleComponentFactory,
    handleChange, handleChangeFrm, handleChangeSchema, handleCreateFilter, handleDefaultGetList,
    handleDelete, handleDeleteFilter, handleGet, handleGetFilters, handleGetList, handlePrint,
    handleReset, handleSelect, handleUpdateFilter, handleUpdateSort, hidden, isHidden, hiddenBack,
    isLoading, isUserFilter, onGetSchema, onSubGridDetailRow, parent, parentProps, primaryField, printItems,
    reducer, rowProps, getSelectActions, selected, setData, showAbort, subGridDetailRow, tlac, total, userFilterList, userFilterExtended, args, showMsg 
  ]);

  useEffect(() => {
    const newObj = {
      processReducer: processReducer,
      get: _get,
      getObj: getObj,
      showMsg: showMsg,
      ...gridProps
    };

    objRef.current = newObj;
  }, [_get, processReducer, getObj, showMsg, gridProps]);

  const isEditable = isCellEdit || isInlineEdit;

  const currentEl = currentElFactory ? currentElFactory(primaryField, showMsg, getObj(null)) : null;

  const metaTabs = useMemo(() => (<Tabs name={primaryField}>
    {[
      <Tab title="Zoznam" key="grid">
        {isEditable ?
          <ExtendedDataGrid
            {...gridProps}
            onSubmit={handleDefaultGetList}
            oldData={oldData}
            onItemChange={handleItemChange}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onAdd={((isCellEdit || isInlineEdit) && isHidden(flags.DETAIL)) ? handleAdd : null}
            onCopy={((isCellEdit || isInlineEdit) && isHidden(flags.DETAIL)) ? handleCopy : null}
            onAbortChangesAll={handleAbortChangesAll}
            onAbortChanges={handleAbortChanges}
            enterEdit={handleEnterEdit}
            exitEdit={handleExitEdit}
            showToolbarButtons={isCellEdit}
            cellSelect={isCellEdit}
            editableCell={isCellEdit}
            modified={modified}
          />
          :
          <DataGrid
            {...gridProps}
          />}
      </Tab>,
      isHidden(flags.DETAIL) ? null :
        <Tab title="Detail" key="detail" hideStandardInactive>
          <Detail
            onSubmit={() => { }}
            detailData={detailData}
            onChangeField={changeField}
            allData={data}
            primaryField={primaryField}
            schema={detailSchema}
            ui={ui}
            args={args}
            pages={pages}
            onGet={handleGet}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            isDetailLoading={isDetailLoading}
            getList={handleGetList}
            toolbarActions={getDetailActions}
            disabled={isDisabled(flags.DETAIL) || isDetailDisabled || (isHiddenORDisabled(flags.UPDATE) && detailData[dataPrimaryField])}
            hiddenSave={isHiddenORDisabled(flags.UPDATE) && detailData[dataPrimaryField]}
            akcDisabled={disabled}
            akcHidden={hidden}
            akc={state.akc}
            onDiscard={discardChanges}
            onBlur={blur}
            modified={modified}
            componentFactory={handleComponentFactory}
            currentEl={currentEl}
            akcFactory={akcFactory}
            frms={frms}
            handleChangeFrm={handleChangeFrm}
            reducer={reducer}
            sidebarHorizontal={sidebarHorizontal}
            parentProps={parentProps}
          />
        </Tab>,
      // tabs je občas z neznámeho dôvodu undefined
      ...(tabs ?? []).map((tab, i) => {
        return (
          <Tab title={tab.name} key={`${tab.name}-${i}`} disabled={tab.iZobraz === 2}>
            {getComponent(tab.iKomponent, currentState.current)}
          </Tab>
        );
      })
    ]}
  </Tabs>
  ), [frms, akcFactory, args, blur, changeField, handleComponentFactory, currentElFactory, data, detailData,
    detailSchema, discardChanges, disabled, getComponent, getDetailActions, getObj, gridProps,
    handleAbortChanges, handleChangeFrm, parentProps, reducer, handleAbortChangesAll, handleAdd, handleCopy, handleCreate,
    handleDefaultGetList, handleEnterEdit, handleExitEdit, handleGet, handleGetList,
    handleItemChange, handleUpdate, hidden, isCellEdit, isDetailDisabled, isDetailLoading,
    isDisabled, isEditable, isHidden, isInlineEdit, modified, oldData, pages, primaryField,
    showMsg, tabs, ui
  ]);

  //#region Otázky 
  const handleQuestionSuccess = async () => {
    const action = state.questions.action;
    const id = state.questions.id;
    const detailData = currentState.current[primaryField].detailData;
    setState(prev => ({ ...prev, questions: null }));
    switch (action) {
      case "UPDATE":
        await handleUpdate(id, detailData);
        break;
      case "CREATE":
        await handleCreate(detailData);
        break;
      default:
        break;
    }
  };
  //#endregion

  const oldPrimaryField = usePrevious(primaryField); // loading after change primary Field
  if (isInitLoading || oldPrimaryField !== primaryField) {
    return <LoadingPanel isLoading={isInitLoading} />;
  }

  if (isHidden(flags.GRID)) {
    return <div>
      <p>Je nám to ľúto, ale nemáte nastavené práva na zobrazenie mriežky formulára!</p>
    </div>;
  }

  return (
    <>
      <MetaStateProvider state={state} >
        <FilterList
          primaryField={primaryField}
          filter={filter.current}
          userFilter={userFilter.current}
          predefinedFilter={predefinedFilter.current}
          gridSchema={gridSchema}
          frms={frms}
          removeFilter={removeFilter}
          getSchema={onGetSchema}
          db={db}
          name={formName}
          settedFTS={fts.current}
          onChangeFTS={setFTS}
          getList={handleDefaultGetList}
          isFts={!isHidden(flags.FULLTEXT_SEARCH)}
        />
        {metaTabs}
        <Frm
          onClose={() => handleChangeFrm("frm", null)}
          selectedFrm={state.selectedFrm}
          fixedFilter={state.frmFixedFilter}
          componentFactory={handleComponentFactory}
          frmValue={state.frmValue}
          frmSchema={state.frmSchema}
          frmId={state.frmId}
          level={level || 0}
          reducer={reducer}
          filterFactory={filterFactory}
          akcFactory={akcFactory}
          currentElFactory={currentElFactory}
          db={db}
          parent={{ props: props, state: state, obj: getObj() }}
          modal
        />
        <QuestionDialog
          allQuestions={state.questions?.list}
          setState={setState}
          isOpen={state.questions?.list?.length > 0}
          handleSuccess={handleQuestionSuccess}
          processReducerState={processReducerState}
        />
      </MetaStateProvider>
    </>
  );
};

MetaGrid.propTypes = {
  /**
   * Primary Field Key - Identifier of the primary field in the data grid.
   */
  primaryField: PropTypes.string.isRequired,
  /**
   * Hidden - Number specifying actions should be hidden.
   */
  hidden: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]), //TODO: number
  /**
   * Disabled - Number specifying actions should be disabled.
   */
  disabled: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]), //TODO: number
  /**
   * Fixed Filter - Filter object that cannot be removed. This filter will always be applied.
   */
  fixedFilter: PropTypes.object,
  /**
   * Init Filter - Initial filter to be applied when the component loads.
   */
  initFilter: PropTypes.object,
  /**
   * DB - Name of the database from which the data is fetched.
   */
  db: PropTypes.string,
  /**
   * Reducer - Function that processes the state of the component.
   */
  reducer: PropTypes.func,
  /**
   * Component Factory - Function that generates components based on the provided data.
   */
  componentFactory: PropTypes.func,
  /**
   * Akc Factory - Function that generates actions based on the provided data.
   */
  akcFactory: PropTypes.func,
  /**
   * Get Component - Function that returns a component based on the provided identifier.
   */
  getComponent: PropTypes.func,
  /**
   * Filter Factory - Function that generates filters based on the provided data.
   */
  filterFactory: PropTypes.func,
  /**
   * Current El Factory - Function that generates the current element based on the provided data.
   */
  currentElFactory: PropTypes.func,
  /**
   * Select Akc Factory - Function that generates selection actions based on the provided data.
   */
  selectAkcFactory: PropTypes.func,
  /**
   * Level - Number specifying the level of the component in the hierarchy.
   */
  level: PropTypes.number
};

const mapStateToProps = () => ({
});

const mapDispatchToProps = dispatch => ({
  getListSuccess: res => dispatch({ type: 'GET_LIST_SUCCESS', res }),
  getListFailure: error => dispatch({ type: 'GET_LIST_FAILURE', error }),
  getDetailSuccess: res => dispatch({ type: 'GET_SUCCESS', res }),
  getDetailFailure: error => dispatch({ type: 'GET_FAILURE', error }),
  createSuccess: res => dispatch({ type: 'CREATE_SUCCESS', res }),
  createFailure: error => dispatch({ type: 'CREATE_FAILURE', error }),
  updateSuccess: res => dispatch({ type: 'UPDATE_SUCCESS', res }),
  updateFailure: error => dispatch({ type: 'UPDATE_FAILURE', error }),
  cancelSuccess: res => dispatch({ type: 'CANCEL_SUCCESS', res }),
  cancelFailure: error => dispatch({ type: 'CANCEL_FAILURE', error }),
  deleteSuccess: () => dispatch({ type: 'DELETE_SUCCESS' }),
  deleteFailure: error => dispatch({ type: 'DELETE_FAILURE', error }),
  showMsg: (msg, msgType = 'ok', isDialog = false, title) => dispatch({ type: 'SHOW_MSG', msg, msgType, isDialog, title })
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MetaGrid);